import {
  Box,
  Paper,
  Divider,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DynamicAttributeField } from "../../UserManagement/AddNewUser/components/DynamicAttributeField";
import { GetPortalUserAttributeResult } from "../../../services/api/types/users/users";
import { GetUserProfileResult } from "../../../services/api/types/users/users";

type AttributeValueType = string | number | boolean | string[] | null;

export interface MyDetailsFieldConfig {
  key: string;
  attribute: GetPortalUserAttributeResult;
  value: AttributeValueType;
  onChange: (key: string, value: AttributeValueType) => void;
}

interface MyDetailsSectionProps {
  title?: string;
  loading?: boolean;
  fields?: MyDetailsFieldConfig[]; // New: array of field configs
  // Legacy props for backward compatibility
  displayAttributes?: GetPortalUserAttributeResult[];
  attributeValues?: Record<string, AttributeValueType>;
  userDetails?: GetUserProfileResult | null;
  onAttributeChange?: (key: string, value: AttributeValueType) => void;
  emptyMessage?: string;
  gridColumns?: { xs?: string; md?: string };
}

export const MyDetailsSection: React.FC<MyDetailsSectionProps> = ({
  title = "My details",
  loading = false,
  fields,
  // Legacy props
  displayAttributes,
  attributeValues,
  userDetails,
  onAttributeChange,
  emptyMessage = "No additional attributes available.",
  gridColumns = { xs: "1fr", md: "1fr 1fr" },
}) => {
  // Use new fields array if provided, otherwise fall back to legacy props
  const renderFields = fields
    ? fields.map((field) => {
        return (
          <Box key={field.key} sx={{ width: "100%", height: "70px" }}>
            <DynamicAttributeField
              attr={field.attribute}
              value={field.value ?? null}
              setValue={field.onChange}
              disabled={field.attribute.isEditable === false}
            />
          </Box>
        );
      })
    : displayAttributes && attributeValues && onAttributeChange
      ? displayAttributes.map((attr, index) => {
          return (
            <Box
              key={attr.portalUserAttributeUid || attr.attributeKey || index}
            >
              <DynamicAttributeField
                attr={attr}
                value={attributeValues[attr.attributeKey] ?? null}
                setValue={onAttributeChange}
                disabled={attr.isEditable === false}
              />
            </Box>
          );
        })
      : [];

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : renderFields.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridColumns,
            gap: 3,
          }}
        >
          {renderFields}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {emptyMessage}
        </Typography>
      )}
    </Paper>
  );
};
